import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Dialog from "../../CoreComponent/Dialog";
import httpService from "../../common/httpService";
import toast from "react-hot-toast";
import ImageUpload from "../../CoreComponent/ImageUpload";
import { backendUrlImages } from "../../constant/config";
import { DataGrid } from "@mui/x-data-grid";
import { Typography } from "@mui/material";
import Select from "../../CoreComponent/Select";

const SpeakersComponent = () => {
  const navigate = useNavigate();
  const [usersData, setUsersData] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [errorMsg, setErrorMsg] = useState("");
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [userIdForUpload, setUserIdForUpload] = useState(0);
  const [file, setFile] = useState(null);
  const [conferences, setConferences] = useState([]);
  const [conferenceId, setConferenceId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // VISA dialog states - ✅ FIXED
  const [visaDialogOpen, setVisaDialogOpen] = useState(false);
  const [visaForm, setVisaForm] = useState({ url: "" });
  const [visaRow, setVisaRow] = useState(null);
  const [visaSubmitting, setVisaSubmitting] = useState(false);
  const [visaFetching, setVisaFetching] = useState(false);
  const [visaEditable, setVisaEditable] = useState(false);

  const BaseUrl = process.env.REACT_APP_BASE_URL;
  const token = localStorage.getItem("token");
  const PAGE_SIZE = 3;

  const isValidHttpUrl = (value) => {
    try {
      const u = new URL(value);
      return u.protocol === "http:" || u.protocol === "https:";
    } catch {
      return false;
    }
  };

  const safeParseTopics = (t) => {
    if (!t) return null;
    try {
      const parsed = JSON.parse(t);
      return Array.isArray(parsed) ? parsed : null;
    } catch {
      return null;
    }
  };

  // ✅ FIXED - Extract URL from API response (handles multiple formats)
  const extractVisaUrl = (data) => {
    if (!data) return "";
    return (
      data?.url ||
      data?.visa_url ||
      data?.data?.url ||
      data?.data?.[0]?.url ||
      data?.data?.visa?.url ||
      data?.results?.[0]?.url ||
      ""
    );
  };

  // ✅ FIXED - YOUR API: https://eventscons.com/eventbackendnew/api/visa?user_id={user_id}
  const fetchLastVisaUrl = async (row) => {
    if (!row?.user_id) {
      setVisaForm({ url: "" });
      return;
    }

    try {
      setVisaFetching(true);

      const response = await httpService({
        method: "GET",
        url: `https://eventscons.com/eventbackendnew/api/visa?user_id=${row.user_id}`,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        withToast: false,
      });

      const lastUrl = extractVisaUrl(response);
      setVisaForm({ url: lastUrl || "" });
      
    } catch (error) {
      console.error("Visa URL fetch error:", error);
      setVisaForm({ url: "" });
      toast.error("No Link Added Yet");
    } finally {
      setVisaFetching(false);
    }
  };

  const fetchSpeakers = async (page = 1) => {
    try {
      await httpService({
        method: "GET",
        url: `${BaseUrl}/speakers`,
        params: { page, conference_id: conferenceId?.value || null },
        headers: { Authorization: `Bearer ${token}` },
        onSuccess: (data) => {
          setUsersData(data.speakers || []);
          setTotalPages(data?.totalPages || 1);
          setCurrentPage(Number(data?.currentPage) || 1);
        },
        onError: (error) =>
          setErrorMsg(error?.message || "Failed to fetch speakers."),
        withToast: true,
      });
    } catch {
      setErrorMsg("Failed to fetch speakers.");
    }
  };

  const handlePageChange = (page) => {
    fetchSpeakers(page);
  };

  const handleUploadClick = (user) => {
    setUserIdForUpload(user.id);
    setDialogOpen(true);
  };

  const handleDelete = async (speakerId) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this speaker?"
    );
    if (!confirmed) return;

    try {
      setDeletingId(speakerId);
      await httpService({
        method: "DELETE",
        url: `${BaseUrl}/speakers/${speakerId}`,
        headers: { Authorization: `Bearer ${token}` },
        onSuccess: () => {
          toast.success("Speaker deleted successfully");
          fetchSpeakers(currentPage);
        },
        onError: (error) => {
          toast.error(error?.message || "Failed to delete speaker");
        },
        withToast: false,
      });
    } catch {
      toast.error("Failed to delete speaker");
    } finally {
      setDeletingId(null);
    }
  };

  // ✅ FIXED - Visa Dialog handlers
  const openVisaDialog = (row) => {
    setVisaRow(row);
    setVisaEditable(false);
    setVisaForm({ url: "" });
    setVisaDialogOpen(true);
  };

  // ✅ Auto-fetch URL when dialog opens
  useEffect(() => {
    if (visaDialogOpen && visaRow?.user_id) {
      fetchLastVisaUrl(visaRow);
    }
  }, [visaDialogOpen, visaRow]);

  const handleVisaEditToggle = () => {
    setVisaEditable(!visaEditable);
  };

  const handleVisaSubmit = async () => {
    if (!visaRow?.user_id) {
      toast.error("Invalid speaker selection");
      return;
    }
    
    if (visaEditable && (!visaForm.url || !isValidHttpUrl(visaForm.url))) {
      toast.error("Please enter a valid http(s) URL");
      return;
    }

    const payload = {
      user_id: visaRow.user_id,
      speaker_id: visaRow.id,
      conference_id: visaRow.conference_id,
      url: visaForm.url,
    };

    try {
      setVisaSubmitting(true);

      await httpService({
        method: "POST",
        url: "https://eventscons.com/eventbackendnew/api/speaker-urls",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: payload,
        onSuccess: () => {
          toast.success("VISA URL saved successfully");
          setVisaDialogOpen(false);
          setVisaEditable(false);
          setVisaForm({ url: "" });
        },
        onError: (error) => {
          toast.error(error?.message || "Failed to save VISA URL");
        },
        withToast: false,
      });
    } catch (error) {
      toast.error("Failed to save VISA URL");
    } finally {
      setVisaSubmitting(false);
    }
  };

  // ✅ COMPLETE COLUMNS with all your fields
  const columns = useMemo(
    () => [
      {
        field: "name",
        headerName: "Name",
        flex: 1,
        minWidth: 230,
        cellClassName: "centered-cell",
      },
      {
        field: "email",
        headerName: "Email",
        flex: 1,
        minWidth: 230,
        cellClassName: "centered-cell",
      },
      {
        field: "biography",
        headerName: "Biography",
        flex: 1,
        minWidth: 230,
        cellClassName: "centered-cell",
      },
      {
        field: "image",
        headerName: "Image",
        flex: 1,
        minWidth: 230,
        cellClassName: "centered-cell",
        renderCell: (params) => {
          const imageUrl = `${backendUrlImages}${params.row.image}`;
          return (
            <div style={{ textAlign: "center" }}>
              <a href={imageUrl} target="_blank" rel="noopener noreferrer">
                <img
                  src={imageUrl}
                  alt={params.row.name}
                  style={{
                    width: "50px",
                    height: "50px",
                    borderRadius: "50%",
                    objectFit: "cover",
                  }}
                />
              </a>
            </div>
          );
        },
      },
      {
        field: "abstract",
        headerName: "Abstract",
        flex: 1,
        minWidth: 230,
        cellClassName: "centered-cell",
        renderCell: (params) =>
          params.row.abstract ? (
            <a
              href={`${backendUrlImages}${params.row.abstract}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              View Abstract
            </a>
          ) : (
            "No Abstract"
          ),
      },
      {
        field: "presentation_file",
        headerName: "Presentation File",
        flex: 1,
        minWidth: 230,
        cellClassName: "centered-cell",
        renderCell: (params) =>
          params.row.presentation_file ? (
            <a
              href={`${backendUrlImages}${params.row.presentation_file}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              View Presentation
            </a>
          ) : (
            "No Presentation"
          ),
      },
      {
        field: "video",
        headerName: "Video",
        flex: 1,
        minWidth: 230,
        cellClassName: "centered-cell",
        renderCell: (params) =>
          params.row.video ? (
            <a
              href={`${backendUrlImages}${params.row.video}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              View Video
            </a>
          ) : (
            "No Video"
          ),
      },
      {
        field: "second_abstract",
        headerName: "Second Abstract",
        flex: 1,
        minWidth: 230,
        cellClassName: "centered-cell",
        renderCell: (params) =>
          params.row.second_abstract ? (
            <a
              href={`${backendUrlImages}${params.row.second_abstract}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              View Second Abstract
            </a>
          ) : (
            "No Second Abstract"
          ),
      },
      {
        field: "second_presentation_file",
        headerName: "Second Presentation File",
        flex: 1,
        minWidth: 230,
        cellClassName: "centered-cell",
        renderCell: (params) =>
          params.row.second_presentation_file ? (
            <a
              href={`${backendUrlImages}${params.row.second_presentation_file}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              View Second Presentation
            </a>
          ) : (
            "No Second Presentation"
          ),
      },
      {
        field: "second_video",
        headerName: "Second Video",
        flex: 1,
        minWidth: 230,
        cellClassName: "centered-cell",
        renderCell: (params) =>
          params.row.second_video ? (
            <a
              href={`${backendUrlImages}${params.row.second_video}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              View Second Video
            </a>
          ) : (
            "No Second Video"
          ),
      },
      {
        field: "online_participation",
        headerName: "Online Participation",
        flex: 1,
        minWidth: 230,
        cellClassName: "centered-cell",
      },
      {
        field: "is_online_approved",
        headerName: "Allowed Online",
        flex: 1,
        minWidth: 230,
        cellClassName: "centered-cell",
      },
      {
        field: "dinner_invitation",
        headerName: "Have Dinner Invitation",
        flex: 1,
        minWidth: 230,
        cellClassName: "centered-cell",
      },
      {
        field: "airport_pickup",
        headerName: "Free Airport Pickup",
        flex: 1,
        minWidth: 230,
        cellClassName: "centered-cell",
      },
      {
        field: "free_trip",
        headerName: "Free Trip",
        flex: 1,
        minWidth: 230,
        cellClassName: "centered-cell",
      },
      {
        field: "topic",
        headerName: "Topic",
        flex: 1,
        minWidth: 150,
        cellClassName: "centered-cell",
        renderCell: (params) => (
          <button
            style={{
              backgroundColor: "green",
              color: "white",
              border: "none",
              padding: "5px 10px",
              borderRadius: "3px",
              cursor: "pointer",
            }}
            onClick={() =>
              navigate(
                `/speakers-topic/${params.row.id}/${params.row.user_id}/${params.row.conference_id}`
              )
            }
          >
            View
          </button>
        ),
      },
      {
        field: "add_visa",
        headerName: "Add VISA",
        flex: 1,
        minWidth: 150,
        cellClassName: "centered-cell",
        renderCell: (params) => (
          <button
            style={{
              backgroundColor: "#8e24aa",
              color: "white",
              border: "none",
              padding: "5px 10px",
              borderRadius: "3px",
              cursor: "pointer",
              opacity: visaDialogOpen && visaRow?.id === params.row.id ? 0.7 : 1,
            }}
            disabled={visaDialogOpen && visaRow?.id === params.row.id}
            onClick={(e) => {
              e.stopPropagation();
              openVisaDialog(params.row);
            }}
          >
            Add VISA
          </button>
        ),
      },
      {
        field: "delete",
        headerName: "Delete",
        flex: 1,
        minWidth: 150,
        cellClassName: "centered-cell",
        renderCell: (params) => (
          <button
            style={{
              backgroundColor: "red",
              color: "white",
              border: "none",
              padding: "5px 10px",
              borderRadius: "3px",
              cursor: "pointer",
              opacity: deletingId === params.row.id ? 0.7 : 1,
            }}
            disabled={deletingId === params.row.id}
            onClick={() => handleDelete(params.row.id)}
          >
            {deletingId === params.row.id ? "Deleting..." : "Delete"}
          </button>
        ),
      },
      {
        field: "actions",
        headerName: "Actions",
        flex: 1,
        minWidth: 150,
        cellClassName: "centered-cell",
        renderCell: (params) => (
          <button
            style={{
              backgroundColor: "#1976d2",
              color: "white",
              border: "none",
              padding: "5px 10px",
              borderRadius: "3px",
              cursor: "pointer",
            }}
            onClick={() => navigate(`/speakers-edit/${params.row.id}`)}
          >
            Edit
          </button>
        ),
      },
    ],
    [navigate, deletingId, visaDialogOpen, visaRow]
  );

  const rows = usersData.map((user) => ({
    ...user,
    name: user?.user?.name,
    email: user?.user?.email,
    biography: user?.user?.biography,
    image: user?.user?.image,
    abstract: user.abstract,
    presentation_file: user.presentation_file,
    video: user.video,
    topics: user.topics,
    second_abstract: user.second_abstract,
    second_presentation_file: user.second_presentation_file,
    second_video: user.second_video,
    online_participation: user.online_participation ? "Yes" : "No",
    is_online_approved: user.is_online_approved ? "Yes" : "No",
    dinner_invitation:
      user?.conference_dinner &&
      user.conference_dinner.toString().toLowerCase() === "included"
        ? "Yes"
        : "No",
    airport_pickup:
      user?.transportation &&
      ["one way", "two ways"].includes(
        user.transportation.toString().toLowerCase()
      )
        ? "Yes"
        : "No",
    free_trip:
      user?.social_trip &&
      user.social_trip.toString().toLowerCase() === "included"
        ? "Yes"
        : "No",
    topic: user.topics ? safeParseTopics(user.topics) : "No Topic",
    user_id: user.user_id,
    conference_id: user.conference_id,
  }));

  const handleFileUpload = async () => {
    const formData = new FormData();
    formData.append("certificatePDF", file);

    try {
      await httpService({
        method: "POST",
        url: `${BaseUrl}/users/${userIdForUpload}/certificate`,
        headers: { Authorization: `Bearer ${token}` },
        data: formData,
        onSuccess: () => {
          toast.success("File uploaded successfully!");
          fetchSpeakers(currentPage);
        },
        onError: (error) => console.error("Failed to upload file:", error),
      });
    } catch (error) {
      console.error("Failed to upload file:", error);
    }
    setDialogOpen(false);
  };

  const fetchConferences = async () => {
    try {
      await httpService({
        method: "GET",
        url: `${BaseUrl}/conferences/all`,
        onSuccess: (data) => {
          setConferences(data.data);
        },
        onError: (error) =>
          setErrorMsg(error?.message || "Failed to fetch conferences."),
      });
    } catch {
      setErrorMsg("Failed to fetch conferences.");
    }
  };

  useEffect(() => {
    fetchConferences();
  }, []);

  useEffect(() => {
    fetchSpeakers();
  }, [conferenceId]);

  return (
    <div style={{ borderRadius: "8px", width: "100%", maxWidth: "1700px", padding: "20px" }}>
      <Typography
        variant="h6"
        sx={{
          color: "#c62828",
          fontWeight: "bold",
          fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
          textAlign: "center",
        }}
      >
        All Speakers
      </Typography>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: "20px",
          gap: "20px",
        }}
      >
        <div style={{ flex: 1 }}>
          <Select
            label="Select Conference"
            options={conferences.map((item) => ({
              label: item.title,
              value: item.id,
            }))}
            value={conferenceId}
            setValue={setConferenceId}
          />
        </div>

        <button
          style={{
            backgroundColor: "#9B1321",
            color: "white",
            padding: "6px 20px",
            borderRadius: "3px",
            border: "none",
            cursor: "pointer",
            fontWeight: "bold",
            marginTop: "30px",
          }}
          onClick={() => navigate("/speakers-add")}
        >
          Add Speakers
        </button>
      </div>

      <DataGrid
        rows={rows}
        columns={columns}
        getRowId={(row) => row.id}
        paginationModel={{ page: currentPage - 1, pageSize: PAGE_SIZE }}
        onPaginationModelChange={(pagination) => {
          setCurrentPage(pagination.page + 1);
          handlePageChange(pagination.page + 1);
        }}
        rowCount={totalPages * PAGE_SIZE}
        pageSizeOptions={[PAGE_SIZE]}
        paginationMode="server"
        checkboxSelection
        disableRowSelectionOnClick
        autoHeight
        sx={{ 
          marginTop: "20px",
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#f5f5f5',
          },
          '& .centered-cell': {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }
        }}
      />

      {/* Certificate upload dialog */}
      {isDialogOpen && (
        <Dialog
          viewHeader={true}
          header="Upload Certificate File"
          open={isDialogOpen}
          setOpen={setDialogOpen}
          className="dialog-file"
        >
          <div className="dialog-content">
            <ImageUpload
              required
              label="Certificate File"
              allowedExtensions={["pdf", "jpg", "jpeg", "png"]}
              inputValue={file}
              setInputValue={setFile}
              className="image-upload"
              placeholder="Choose a file"
            />
            <div className="dialog-actions">
              <button className="save-button" onClick={handleFileUpload}>
                Save
              </button>
              <button className="cancel-button" onClick={() => setDialogOpen(false)}>
                Cancel
              </button>
            </div>
          </div>
        </Dialog>
      )}

      {/* ✅ FIXED COMPLETE VISA DIALOG */}
      {visaDialogOpen && (
        <Dialog
          viewHeader={true}
          header="Add VISA Details"
          open={visaDialogOpen}
          setOpen={setVisaDialogOpen}
          className="dialog-file"
        >
          <div className="dialog-content" style={{ padding: "20px" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              <label htmlFor="visa-url">
                VISA URL for {visaRow?.name || "Speaker"}
              </label>
              <input
                id="visa-url"
                type="url"
                placeholder={
                  visaFetching 
                    ? "Fetching last VISA URL..." 
                    : "https://example.com/speaker/visa.pdf"
                }
                value={visaForm.url}
                onChange={(e) => setVisaForm({ url: e.target.value })}
                disabled={!visaEditable || visaFetching || visaSubmitting}
                style={{
                  padding: "12px",
                  border: visaEditable && visaForm.url && !isValidHttpUrl(visaForm.url) 
                    ? "2px solid #f44336" 
                    : "1px solid #ccc",
                  borderRadius: "4px",
                  fontSize: "14px",
                }}
              />
              {visaEditable && visaForm.url && !isValidHttpUrl(visaForm.url) && (
                <span style={{ color: "#f44336", fontSize: "12px" }}>
                  Enter valid http(s) URL
                </span>
              )}
            </div>

            <div style={{ display: "flex", gap: "10px", marginTop: "15px" }}>
              {!visaEditable && (
                <button
                  type="button"
                  onClick={handleVisaEditToggle}
                  disabled={visaFetching || visaSubmitting}
                  style={{
                    backgroundColor: "#455a64",
                    color: "white",
                    padding: "8px 16px",
                    borderRadius: "4px",
                    border: "none",
                    cursor: "pointer",
                    fontWeight: "bold",
                  }}
                >
                   Edit
                </button>
              )}
              <button
                type="button"
                onClick={() => fetchLastVisaUrl(visaRow)}
                disabled={visaFetching || visaSubmitting}
                style={{
                  backgroundColor: "#6d4c41",
                  color: "white",
                  padding: "8px 16px",
                  borderRadius: "4px",
                  border: "none",
                  cursor: "pointer",
                  fontWeight: "bold",
                }}
              >
                Refresh
              </button>
            </div>

            <div className="dialog-actions" style={{ marginTop: "20px" }}>
              {visaEditable && (
                <button
                  className="save-button"
                  style={{
                    backgroundColor: "rgb(155, 19, 33)",
                    color: "white",
                    padding: "10px 24px",
                    borderRadius: "4px",
                    border: "none",
                    cursor: "pointer",
                    fontWeight: "bold",
                    marginRight: "10px",
                  }}
                  onClick={handleVisaSubmit}
                  disabled={visaSubmitting || visaFetching || !isValidHttpUrl(visaForm.url)}
                >
                  {visaSubmitting ? " Saving..." : " Save URL"}
                </button>
              )}
              <button
                className="cancel-button"
                style={{
                  backgroundColor: "rgba(255, 2, 27, 1)",
                  color: "white",
                  padding: "10px 24px",
                  borderRadius: "4px",
                  border: "none",
                  cursor: "pointer",
                  fontWeight: "bold",
                }}
                onClick={() => {
                  setVisaDialogOpen(false);
                  setVisaEditable(false);
                  setVisaForm({ url: "" });
                }}
                disabled={visaSubmitting || visaFetching}
              >
                 Cancel
              </button>
            </div>
          </div>
        </Dialog>
      )}
    </div>
  );
};

export default SpeakersComponent;
